/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import type { CoreAppStore, uiTypes } from '@opengeoweb/store';
import {
  getSingularDrawtoolDrawLayerId,
  layerActions,
  layerSelectors,
  layerUtils,
  uiActions,
  uiSelectors,
  useSetupDialog,
} from '@opengeoweb/store';
import { emptyGeoJSON } from '@opengeoweb/webmap-react';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { ObjectManager } from './ObjectManager';
import {
  useDrawingDetails,
  useDrawings,
  useDrawingsPredefinedAreas,
} from '../../api/warning-api/hooks';
import { warningsDrawingsActions } from '../../store/warningsDrawings/reducer';
import { selectDrawByInstanceId } from '../../store/warningsDrawings/selectors';
import type {
  AreaListItem,
  DrawingListItem,
} from '../../store/warningsDrawings/types';
import {
  drawingDialogType,
  objectDialogType,
} from '../../store/warningsDrawings/utils';
import { warningsFormActions } from '../../store/warningsForm/reducer';
import type { PublicWarningDetail } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';
import { useObjectDrawDialogAction } from '../DrawingTool/useObjectDrawDialogAction';

export const ObjectManagerConnect: React.FC<{
  bounds?: string;
  source?: uiTypes.Source;
}> = ({ bounds, source = 'app' }) => {
  const { isLoggedIn } = useAuthenticationContext();
  const { t } = useWarningsTranslation();
  const { dialogOrder, setDialogOrder, isDialogOpen, uiSource, uiError } =
    useSetupDialog(objectDialogType, source);
  const [selectedDrawingId, setSelectedDrawingId] = useState<
    string | undefined
  >();

  // By default, select Areas (0)
  // While in development, autoselect Objects as that is familiar to the users
  const [selectedTab, setSelectedTab] = React.useState(1);

  const {
    data: drawingDetails,
    isFetching: isFetchingDetails,
    error: drawingError,
  } = useDrawingDetails(selectedDrawingId!);

  const dispatch = useDispatch();
  const mapId = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, objectDialogType),
  );
  const { closeObjectDialog, openDrawDialog } = useObjectDrawDialogAction({
    mapId,
    source,
  });
  const geoJSONLayerId = getSingularDrawtoolDrawLayerId(mapId);

  const currentLayer = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerByIdWithoutTimeDimension(store, geoJSONLayerId),
  );
  const currrentDrawing = useSelector((store: CoreAppStore) =>
    selectDrawByInstanceId(store, drawingDialogType),
  );
  const activeDrawingId = currrentDrawing?.id || '';
  const selectedAreaFeatureNamesByAreaId = React.useMemo(() => {
    const selectedFeatureIdSet = new Set(
      currentLayer?.selectedFeatureIds ?? [],
    );
    const selectedIndices =
      currentLayer?.geojson?.features.reduce<number[]>(
        (indices, feature, index) => {
          const featureKey = layerUtils.getFeatureSelectionKey(feature);
          if (featureKey && selectedFeatureIdSet.has(featureKey)) {
            indices.push(index);
          }
          return indices;
        },
        [],
      ) ?? [];

    if (selectedIndices.length === 0) {
      return {};
    }

    const result: Record<string, string[]> = {};

    selectedIndices.forEach((selectedFeatureIndex) => {
      if (selectedFeatureIndex === undefined) {
        return;
      }

      const selectedFeature =
        currentLayer?.geojson?.features[selectedFeatureIndex];
      const areaId = selectedFeature?.properties?.areaId;
      if (!areaId || typeof areaId !== 'string') {
        return;
      }

      const selectedFeatureName =
        selectedFeature.properties?.objectName ??
        selectedFeature.properties?.name ??
        selectedFeature.id ??
        selectedFeatureIndex;

      if (!result[areaId]) {
        result[areaId] = [];
      }
      result[areaId].push(String(selectedFeatureName));
    });

    return result;
  }, [currentLayer]);

  const {
    data: drawings = [],
    error,
    isFetching: isFetchingList,
  } = useDrawings({}, isDialogOpen);

  const {
    data: predefinedAreas = [],
    error: predefinedAreasError,
    isFetching: isFetchingPredefinedAreasList,
  } = useDrawingsPredefinedAreas(isDialogOpen);

  useEffect(() => {
    if (error || predefinedAreasError || !isLoggedIn) {
      const currentError = error || predefinedAreasError;
      const message = currentError?.message ?? t('error-auth-login');
      dispatch(
        uiActions.setErrorDialog({
          error: message,
          type: objectDialogType,
        }),
      );
    }
  }, [error, predefinedAreasError, isLoggedIn, dispatch, t]);

  const deleteGeoJSONLayer = useCallback((): void => {
    if (geoJSONLayerId && currentLayer) {
      dispatch(
        layerActions.layerChangeGeojson({
          layerId: geoJSONLayerId,
          geojson: emptyGeoJSON,
        }),
      );
      dispatch(
        layerActions.toggleFeatureMode({
          layerId: geoJSONLayerId,
          isInEditMode: false,
        }),
      );
    }
  }, [geoJSONLayerId, currentLayer, dispatch]);

  const showGeoJSONLayer = useCallback(
    (drawing: DrawingListItem | AreaListItem, isArea = false): void => {
      const geoJSON = {
        ...drawing.geoJSON,
        features: drawing.geoJSON.features.map((feature, index) => ({
          ...feature,
          id: feature.id ?? `${drawing.id}-${index}`,
          properties: {
            ...feature.properties,
            areaId: drawing.id,
            areaFeatureName:
              feature.properties?.objectName ??
              feature.properties?.name ??
              feature.id,
            isReadonly: true,
            mapFeatureClass: 'default',
            isSelected: false,
            fill: '#186DFF',
            'fill-opacity': isArea ? 0.0 : 0.2,
            stroke: '#006CFD',
          },
        })),
      };

      if (!currentLayer) {
        const layerPayload = {
          geojson: geoJSON,
          isSelectable: isArea,
          isInEditMode: false,
          enabled: true,
        };
        dispatch(
          layerActions.addLayer({
            mapId,
            layer: layerPayload,
            layerId: geoJSONLayerId,
            origin: 'warnings-lib',
          }),
        );
      } else {
        dispatch(
          layerActions.layerChangeGeojson({
            layerId: geoJSONLayerId,
            geojson: geoJSON,
          }),
        );
        dispatch(
          layerActions.toggleFeatureMode({
            layerId: geoJSONLayerId,
            isInEditMode: false,
          }),
        );
        dispatch(
          layerActions.setSelectedFeatures({
            layerId: geoJSONLayerId,
            selectedFeatureIds: [],
          }),
        );
        if (currentLayer.isSelectable !== isArea) {
          dispatch(
            layerActions.toggleSelectableFeatures({ layerId: geoJSONLayerId }),
          );
        }
      }
    },
    [currentLayer, dispatch, mapId, geoJSONLayerId],
  );

  const onClickDrawing = async (drawing: DrawingListItem): Promise<void> => {
    const isActive = drawing.id === currrentDrawing?.id;

    dispatch(
      warningsDrawingsActions.setDrawValues({
        objectName: drawing.objectName,
        drawingInstanceId: drawingDialogType,
        id: !isActive ? drawing.id : '',
      }),
    );

    if (isActive) {
      deleteGeoJSONLayer();
    } else {
      showGeoJSONLayer(drawing);
    }
  };

  const onClickArea = useCallback(
    (drawing: AreaListItem): void => {
      const isActive = drawing.id === currrentDrawing?.id;

      dispatch(
        warningsDrawingsActions.setDrawValues({
          objectName: drawing.objectName,
          drawingInstanceId: drawingDialogType,
          id: !isActive ? drawing.id : '',
        }),
      );

      if (isActive) {
        deleteGeoJSONLayer();
      } else {
        showGeoJSONLayer(drawing, true);
      }
    },
    [currrentDrawing?.id, dispatch, deleteGeoJSONLayer, showGeoJSONLayer],
  );

  const [confirmDialogOptions, setConfirmDialogOptions] = React.useState<
    | {
        objectId: string;
        objectName: string;
      }
    | undefined
  >(undefined);

  const openDrawTools = (drawingId: string): void => {
    dispatch(
      uiActions.setErrorDialog({
        error: '',
        type: objectDialogType,
      }),
    );

    if (drawingId !== activeDrawingId) {
      deleteGeoJSONLayer();
    }

    setSelectedDrawingId(drawingId);
  };

  useEffect(() => {
    if (
      !selectedDrawingId ||
      !drawingDetails ||
      drawingDetails?.id !== selectedDrawingId ||
      isFetchingDetails
    ) {
      return;
    }

    dispatch(
      warningsDrawingsActions.setDrawValues({
        objectName: drawingDetails.objectName,
        id: selectedDrawingId,
        drawingInstanceId: drawingDialogType,
      }),
    );

    openDrawDialog(drawingDetails.geoJSON);
    setSelectedDrawingId(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDrawingId, drawingDetails, dispatch, isFetchingDetails]);

  useEffect(() => {
    if (drawingError) {
      dispatch(
        uiActions.setErrorDialog({
          error: drawingError.message,
          type: objectDialogType,
        }),
      );
    }
  }, [dispatch, drawingError]);

  useEffect(() => {
    const selectedFeatureIndex = layerUtils.getSelectedFeatureIndexByIds(
      currentLayer?.geojson,
      currentLayer?.selectedFeatureIds,
    );

    if (selectedFeatureIndex === undefined) {
      return;
    }

    const selectedFeature =
      currentLayer?.geojson?.features[selectedFeatureIndex];

    if (!selectedFeature) {
      return;
    }

    const areaId = selectedFeature.properties?.areaId;
    if (!areaId) {
      return;
    }

    const area = predefinedAreas.find(
      (predefinedArea) => predefinedArea.id === areaId,
    );

    if (area && area.id !== currrentDrawing?.id) {
      onClickArea(area);
    }
  }, [currentLayer, currrentDrawing, onClickArea, predefinedAreas]);

  const onEditDrawing = (drawingId: string): void => {
    void openDrawTools(drawingId);
  };

  const onClose = (): void => {
    closeObjectDialog();
  };

  const onShareObject = (object: DrawingListItem): void => {
    dispatch(
      warningsFormActions.openPublicWarningFormDialog({
        object,
        warning: {
          warningDetail: {
            areas: [{ geoJSON: object.geoJSON, objectName: object.objectName }],
          } as PublicWarningDetail,
          type: 'public',
        },
      }),
    );
  };

  const onCreateNewObject = (): void => {
    closeObjectDialog();
    openDrawDialog();
  };

  const onDeleteSuccess = (objectId: string): void => {
    // close confirm dialog
    setConfirmDialogOptions(undefined);
    // remove any geoJSON layer of deleted object if set
    if (activeDrawingId && activeDrawingId === objectId) {
      deleteGeoJSONLayer();
    }
  };

  const onDeleteDrawing = (objectId: string): void => {
    const currentObject = drawings.find((drawing) => drawing.id === objectId);
    const objectName = currentObject?.objectName!;

    setConfirmDialogOptions({ objectId, objectName });
  };

  const handleTabChange = useCallback(
    (newTab: number): void => {
      if (newTab !== selectedTab) {
        deleteGeoJSONLayer();
        dispatch(
          warningsDrawingsActions.setDrawValues({
            objectName: '',
            drawingInstanceId: drawingDialogType,
            id: '',
          }),
        );
      }
      setSelectedTab(newTab);
    },
    [selectedTab, deleteGeoJSONLayer, dispatch],
  );

  return (
    <>
      <ObjectManager
        activeDrawingId={activeDrawingId}
        bounds={bounds}
        drawingListItems={drawings}
        error={uiError}
        headerSize="xs"
        isLoading={
          isFetchingDetails || isFetchingList || isFetchingPredefinedAreasList
        }
        isOpen={isDialogOpen}
        onChangeTab={handleTabChange}
        onClickArea={onClickArea}
        onClickDrawing={onClickDrawing}
        onClose={onClose}
        onCreateNewObject={onCreateNewObject}
        onDeleteDrawing={onDeleteDrawing}
        onEditDrawing={onEditDrawing}
        onMouseDown={setDialogOrder}
        onShareObject={onShareObject}
        order={dialogOrder}
        predefinedAreaListItems={predefinedAreas}
        selectedAreaFeatureNamesByAreaId={selectedAreaFeatureNamesByAreaId}
        selectedTab={selectedTab}
        source={uiSource}
        startPosition={{ top: 104, left: 50 }}
        title={`${t('object-manager-title')} ${mapId}`}
      />
      {!!confirmDialogOptions && (
        <ConfirmDeleteDialog
          {...confirmDialogOptions}
          onClose={(): void => setConfirmDialogOptions(undefined)}
          onDeleteSucces={onDeleteSuccess}
        />
      )}
    </>
  );
};
